// uiZoomCoords — pointer coordinates for an overlay that lives under a CSS zoom.
//
// The app root carries `zoom` for the UI-scale setting (uiScale.js), and the
// DEFAULT step is 1.1 — so this applies to essentially every user, not to an
// exotic setting. Two coordinate spaces result and they are easy to mix up:
//
//   MouseEvent.clientX/Y, window.innerWidth/Height   → unzoomed viewport px
//   an element's own left/top under a zoom            → zoomed px
//
// Assigning the first to the second renders the overlay at clientX * zoom.
// Measured on the asset context menu at the default scale: right-clicking at
// (622, 634) wrote `left: 622px` and painted at 684 — a 62px drift down-right
// of the cursor, growing with distance from the origin. The clamp that keeps
// the menu on-screen was wrong in the same way, in the other direction: it
// compared a zoomed box against an unzoomed innerWidth and so believed it had
// 10% more room than it had.
//
// The conversion is CUMULATIVE and per-element, never a global constant. Nested
// zooms multiply, and this app deliberately nests them: `.react-flow` undoes the
// root zoom (styles/index.css) so React Flow's two coordinate spaces agree, and
// inside that subtree the net zoom is 1 and a raw clientX was already correct.
// Asking the element makes the same call site right in both places — and makes
// the helper a no-op wherever the net zoom is 1, which is what allows it to be
// applied blanket-wise without any risk of double-correcting.

/**
 * Product of a chain of zoom factors, ignoring anything not a usable number.
 * Pure — the arithmetic is here so it can be tested without a DOM.
 */
export function zoomProduct(zooms) {
  let z = 1
  for (const raw of zooms ?? []) {
    const v = typeof raw === 'number' ? raw : parseFloat(raw)
    if (Number.isFinite(v) && v > 0) z *= v
  }
  // A zero or negative product would divide a coordinate into nonsense; a
  // missing/odd value means "we could not tell", and unzoomed is the answer
  // that leaves behaviour exactly as it was before this module existed.
  return z > 0 && Number.isFinite(z) ? z : 1
}

/**
 * The cumulative CSS zoom in effect at `el`.
 *
 * `Element.currentCSSZoom` is the platform's own answer to precisely this
 * question, so it is preferred where implemented; the ancestor walk is the
 * fallback for engines without it. Both are asked at CALL TIME rather than
 * cached: the UI-scale setting changes at runtime.
 */
export function cumulativeZoom(el) {
  if (!el) return 1
  const own = el.currentCSSZoom
  if (typeof own === 'number' && Number.isFinite(own) && own > 0) return own

  const view = el.ownerDocument?.defaultView
  if (!view?.getComputedStyle) return 1
  const zooms = []
  for (let n = el; n; n = n.parentElement) {
    try { zooms.push(view.getComputedStyle(n).zoom) } catch { /* detached */ }
  }
  return zoomProduct(zooms)
}

/**
 * Convert a viewport point (clientX/clientY) into the coordinate space of a
 * `position: fixed` overlay mounted at `el`.
 */
export function pointerToOverlaySpace(x, y, el) {
  const z = cumulativeZoom(el)
  return z === 1 ? { x, y } : { x: x / z, y: y / z }
}

/**
 * The viewport as an overlay at `el` measures it — the right ruler for a clamp
 * written in that overlay's own left/top units.
 */
export function overlayViewport(el, win = typeof window !== 'undefined' ? window : null) {
  if (!win) return { vw: 0, vh: 0, zoom: 1 }
  const zoom = cumulativeZoom(el)
  return { vw: win.innerWidth / zoom, vh: win.innerHeight / zoom, zoom }
}
